import dayjs, { Dayjs } from "dayjs";
import {
  SUPPORTED_EVIDENCE_EXTENSIONS,
  isSupportedEvidenceFile,
} from "@/shared/api/evidenceUpload";
import { MockFileType } from "./types";

const colorByType: Record<MockFileType, string> = {
  report: "primary.main",
  video: "error.main",
  image: "info.main",
  audio: "warning.main",
  document: "success.main",
};

export const fileTypeColor = (type: MockFileType): string => colorByType[type];

export const formatDuration = (seconds?: number): string => {
  if (!seconds) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// Kept in sync with the backend allowlist in
// incidents-cdk/lib/lambda/integration/incidentAPIs/validators/schemas.ts.
const ALLOWED_MIME_PREFIXES = ["video/", "image/", "audio/"];
const ALLOWED_MIME_EXACT = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/rtf",
  "text/rtf",
  "text/plain",
]);
const ALLOWED_EXTENSIONS = new Set([
  // Media, narrowed to what the evidence service can ingest — `mkv`, `wmv` and
  // `wma` were dropped when media moved there: its transcription engine cannot
  // read them, so an upload is rejected at presign. See
  // SUPPORTED_EVIDENCE_EXTENSIONS, which is the authority.
  "mp4", "mov", "avi", "webm", "m4v", "mpg",
  "jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "tif", "tiff", "heic",
  "mp3", "wav", "m4a", "aac", "ogg", "flac", "opus", "mp2",
  "pdf", "doc", "docx", "rtf", "txt",
]);

// The `accept` attribute for the native file picker. Covers the major media
// MIME prefixes plus the doc-family extensions. This is a UX hint — the
// authoritative validation lives in isAllowedFile + the backend.
export const ACCEPT_ATTR =
  "video/*,image/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/rtf,text/rtf,text/plain,.doc,.docx,.rtf,.txt,.pdf";

/**
 * The picker hint for surfaces that CANNOT take media — currently the station
 * file library, which uploads without attaching to an incident.
 *
 * Video and audio become evidence items, and an evidence item is not a station
 * file: it does not appear in the library and cannot be attached through
 * `POST /incidents/{id}/files`. Uploading one here would produce a clip the
 * officer could see nowhere in this dialog. It has to be attached to an incident
 * at upload time, which is what the other two dialogs do.
 */
export const ACCEPT_ATTR_NON_MEDIA =
  "image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/rtf,text/rtf,text/plain,.doc,.docx,.rtf,.txt,.pdf";

export function fileExtension(name: string): string {
  const i = name.lastIndexOf(".");
  if (i < 0 || i === name.length - 1) return "";
  return name.slice(i + 1).toLowerCase();
}

/**
 * True when this file is video or audio, and therefore belongs to the evidence
 * service rather than to the incidents API.
 *
 * Mirrors `isMediaUpload` on the incidents side, which now REJECTS media at
 * `/files/upload-init` — so a mismatch here is not a cosmetic routing slip, it is
 * a 400 the officer cannot act on.
 */
export function isEvidenceMedia(file: File): boolean {
  const type = inferFileType(file);
  if (type === "video" || type === "audio") return true;
  // A file the browser could not type (`application/octet-stream`, or nothing)
  // falls through inferFileType to "document". Its extension is the only signal
  // left, and the incidents API checks the same one.
  return SUPPORTED_EVIDENCE_EXTENSIONS.has(fileExtension(file.name));
}

export function isAllowedFile(file: File): boolean {
  // Media is held to the evidence service's list, not the broader MIME-prefix
  // rule: `video/x-matroska` passes the prefix check and would then be rejected
  // at presign, after the officer had already picked it.
  if (isEvidenceMedia(file)) return isSupportedEvidenceFile(file);
  const mime = (file.type || "").toLowerCase();
  if (ALLOWED_MIME_PREFIXES.some((p) => mime.startsWith(p))) return true;
  if (ALLOWED_MIME_EXACT.has(mime)) return true;
  return ALLOWED_EXTENSIONS.has(fileExtension(file.name));
}

export function inferFileType(file: File): MockFileType {
  const mime = file.type.toLowerCase();
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  if (mime === "application/pdf") {
    return file.name.toLowerCase().includes("report") ? "report" : "document";
  }
  return "document";
}

/** Inclusive day-granularity range check (bounds are local calendar days;
 *  an unset bound is open). Lives here (not in the
 *  dialog) so tests can import it without the component's export set
 *  breaking fast refresh. */
export function fileMatchesDateRange(
  createdAt: string,
  from: Dayjs | null,
  to: Dayjs | null
): boolean {
  const d = dayjs(createdAt);
  if (from && d.isBefore(from.startOf("day"))) return false;
  if (to && d.isAfter(to.endOf("day"))) return false;
  return true;
}

export type QuickRange = "today" | "last7" | "last30";

export const QUICK_RANGES: { value: QuickRange; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
];

export function quickRangeStart(range: QuickRange): Dayjs {
  switch (range) {
    case "today":
      return dayjs().startOf("day");
    case "last7":
      return dayjs().subtract(7, "day").startOf("day");
    case "last30":
      return dayjs().subtract(30, "day").startOf("day");
  }
}

const DATE_FILTER_FMT = "MMM D, YYYY";

/** Text for the date-filter trigger chip — the only place an active range
 *  is legible without opening the popover. Extracted (like
 *  fileMatchesDateRange) because jsdom can't drive the pickers that reach
 *  the custom-range branches. */
export function formatDateFilterLabel(
  activeQuickRange: QuickRange | null,
  from: Dayjs | null,
  to: Dayjs | null
): string {
  if (activeQuickRange) {
    return (
      QUICK_RANGES.find((qr) => qr.value === activeQuickRange)?.label ??
      "Custom"
    );
  }
  if (from && to) {
    return `${from.format(DATE_FILTER_FMT)} – ${to.format(DATE_FILTER_FMT)}`;
  }
  if (from) return `From ${from.format(DATE_FILTER_FMT)}`;
  if (to) return `Until ${to.format(DATE_FILTER_FMT)}`;
  return "Any time";
}
